import { Box, Button, IconButton, Typography } from '@mui/material';
import { LessonMarkingAction, LessonMarkingPanel } from '../LessonMarkingPanel';
import {
  LessonSubmissionAction,
  LessonSubmissionPanel
} from '../LessonSubmissionPanel';

import { ApiPortfolioState } from '../../../dashboard/models/ApiPortfolioState';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LessonDisplayMode } from '../../../content/models/LessonComponent';
import { LessonPointsView } from '../LessonPointsView';
import { LessonStatusLabel } from '../LessonStatusLabel';
import { LessonType } from '../../../content/models/LessonVm';
import React from 'react';
import styles from '../../styles/LessonPlayer.scss';
import { useExternalLessonContext } from '../../../dashboard/models/ExternalLessonContext';
import { useLessonContext } from '../../../content/models/LessonContext';

interface OverviewPanelProps {
  lessonType: LessonType;
  displayMode: LessonDisplayMode;
  inProgress: boolean;
  points: number;
  totalPoints: number;
  completed: boolean;
  showContinue: boolean;
  state: ApiPortfolioState;
  onClose: () => void;
  onReturn: () => void;
  onSubmit: (action: LessonSubmissionAction) => void; //* 'learn' mode only
  onMark: (action: LessonMarkingAction) => void; //* 'assessment' mode only
  onContinue: () => void;
}

const OverviewPanel = (props: OverviewPanelProps) => {
  const { displayMode, state, completed, showContinue } = props;
  const { assessmentRevised } = useLessonContext();
  const { isExternalLesson } = useExternalLessonContext();
  const showPoints =
    !assessmentRevised &&
    props.totalPoints > 0 &&
    (props.lessonType !== 'assessment' ||
      props.state === ApiPortfolioState.AssessmentLock ||
      props.state === ApiPortfolioState.AssessmentRetry);

  return (
    <Box p={2} className={styles.overviewPanel}>
      <div className={styles.lessonOverviewContainer}>
        <div>
          <Button variant="contained" color="primary" onClick={props.onReturn}>
            {props.lessonType !== 'quiz'
              ? 'View Full Lesson' //* 'lesson' | 'assessment'
              : 'View Full Quiz'}
          </Button>
        </div>
        <div className={styles.lessonOverviewContent}>
          {showPoints && (
            <LessonPointsView
              points={props.points}
              assigned={props.totalPoints}
              showPercentage
            />
          )}
          {props.displayMode !== 'mark' && (
            <>
              <LessonStatusLabel
                displayMode={displayMode}
                state={state}
                completed={completed}
              />
              {!isExternalLesson && showContinue && (
                <>
                  <Typography
                    variant="caption"
                    onClick={props.onContinue}
                    className={styles.cursorPointer}
                  >
                    Continue to the next lesson
                  </Typography>
                  <IconButton
                    onClick={props.onContinue}
                    className={styles.expandIcon}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </>
              )}
            </>
          )}
          <span>
            {props.state === ApiPortfolioState.AssessmentSubmit && (
              <LessonSubmissionPanel
                state={state}
                inProgress={props.inProgress}
                onSubmit={props.onSubmit}
              />
            )}
          </span>
        </div>
        {
          /** For learner and preview display */
          props.displayMode !== 'mark' ? (
            /** Render for basic lesson/quiz */
            props.lessonType !== 'assessment' ||
            /** Render for assessment on specific lesson states  */
            (props.lessonType === 'assessment' &&
              props.state !== ApiPortfolioState.AssessmentStart &&
              props.state !== ApiPortfolioState.AssessmentRetry) ? (
              <div>
                {!isExternalLesson && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={props.onClose}
                  >
                    Return to Course
                  </Button>
                )}
              </div>
            ) : (
              <div>
                <LessonSubmissionPanel
                  state={state}
                  inProgress={props.inProgress}
                  onSubmit={props.onSubmit}
                />
              </div>
            )
          ) : (
            /** For Marker view */
            <div>
              <LessonMarkingPanel
                displayMode={displayMode}
                inProgress={props.inProgress}
                state={state}
                completed={completed}
                onMark={props.onMark}
              />
            </div>
          )
        }
      </div>
    </Box>
  );
};

export default OverviewPanel;
